
import type { PropType } from 'vue'
import { defineComponent, toRefs, computed } from 'vue'
import { WeveMultiselect, WeveSkeleton } from '@weve/ui'
import { useQueryClient, useQuery, useMutation } from '@tanstack/vue-query'

import type { Undefined, Tag } from '@/types'
import { useTagService, usePermissions } from '@/use'

export default defineComponent({
  name: 'TagsMultiselect',
  components: {
    WeveMultiselect,
    WeveSkeleton,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: Array as PropType<string[]>,
      required: true,
    },
    label: {
      type: String,
      default: 'Tags',
    },
  },
  emits: {
    input: (value: string[]) => {
      return value.every((item) => typeof item === 'string')
    },
  },
  setup(props, { emit }) {
    const { value } = toRefs(props)
    const { service } = useTagService()
    const queryClient = useQueryClient()
    const { isAdmin: creational } = usePermissions()

    const key = ['tags']
    const { data, isLoading: isQuerying } = useQuery({
      queryKey: key,
      queryFn: () => service.list.bind(service)(),
    })

    const { mutate: mutateAddTag, isPending: isAddingTag } = useMutation({
      mutationFn: service.create.bind(service),
      onSuccess: (tag: Tag) => {
        queryClient.setQueryData<Undefined<Tag[]>>(key, (data) => {
          if (data === undefined) return undefined
          return [...data, tag]
        })
        emit('input', [...value.value, tag.name])
      },
    })

    function createTag(tag: string) {
      mutateAddTag({ name: tag })
    }

    const tags = computed(() => data.value ?? [])

    const loading = computed(() => isQuerying.value || isAddingTag.value)

    return {
      tags,
      creational,
      isQuerying,
      loading,
      createTag,
    }
  },
})
